import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "../icons";
import { DEFAULT_TEMPLATES } from "../lib/messageTemplates";
import {
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from "../../../business/redux/reducers/mcOpsDashboard/actions";

const BLANK = { id: null, channel: "text", name: "", subject: "", body: "" };

// Critical layout kept inline so the modal always centers on a backdrop even if
// the hashed CSS bundle is momentarily out of sync with the JS after a deploy.
const OVERLAY_STYLE = {
  position: "fixed",
  inset: 0,
  zIndex: 240,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "22px 16px",
  background: "rgba(15,23,42,.5)",
  overflowY: "auto",
};
const CARD_STYLE = {
  background: "#fff",
  width: "100%",
  maxWidth: 760,
  borderRadius: 18,
  boxShadow: "0 16px 40px rgba(15,23,42,.24)",
  maxHeight: "92vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

/**
 * Shared SMS/email template library manager. Coordinators create/edit the
 * team-wide custom templates that appear in the Send text / Send email popups
 * (alongside the built-in defaults). Placeholders {client} {coordinator}
 * {moveDate} are filled per job when sending.
 */
export const TemplatesModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const templates = useSelector((s) => s.mcOpsDashboard?.meta?.templates || []);
  const [draft, setDraft] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const isEmail = draft.channel === "email";
  const editing = draft.id != null;
  const valid = draft.name.trim() && draft.body.trim() && (!isEmail || draft.subject.trim());

  // Built-in ("automated") templates first, then the team's custom ones. A
  // custom template with the same name overrides/hides the built-in default.
  const byChannel = (ch) => {
    const custom = templates.filter((t) => t.channel === ch);
    const customNames = new Set(custom.map((t) => t.name));
    const builtin = DEFAULT_TEMPLATES.filter(
      (t) => t.channel === ch && !customNames.has(t.name),
    );
    return [...builtin, ...custom];
  };

  const save = async () => {
    if (!valid || busy) return;
    setBusy(true);
    const payload = {
      channel: draft.channel,
      name: draft.name.trim(),
      subject: isEmail ? draft.subject.trim() : "",
      body: draft.body.trim(),
    };
    try {
      if (editing) await dispatch(updateTemplate(draft.id, payload));
      else await dispatch(createTemplate(payload));
      setDraft(BLANK);
    } catch (e) {
      /* leave the form so the user can retry */
    }
    setBusy(false);
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await dispatch(deleteTemplate(id));
      if (draft.id === id) setDraft(BLANK);
    } catch (e) {
      /* ignore */
    }
    setBusy(false);
  };

  const list = (ch, label) => {
    const items = byChannel(ch);
    return (
      <div className="tpl-col">
        <div className="tpl-col-head">
          <Icon name={ch === "email" ? "mail" : "msg"} /> {label}
          <span className="tpl-count">{items.length}</span>
        </div>
        {items.length ? (
          items.map((t) => (
            <div
              className={`tpl-item${!t.builtin && draft.id === t.id ? " tpl-item-active" : ""}`}
              key={t.builtin ? "b-" + t.channel + "-" + t.name : t.id}
            >
              <div className="tpl-item-main">
                <div className="tpl-item-name">
                  {t.name}
                  {t.builtin ? <span className="tpl-tag">Default</span> : null}
                </div>
                {t.subject ? <div className="tpl-item-sub">{t.subject}</div> : null}
                <div className="tpl-item-body">{t.body}</div>
              </div>
              <div className="tpl-item-acts">
                {t.builtin ? (
                  <button
                    className="tpl-icon-btn"
                    title="Copy & edit as custom"
                    onClick={() =>
                      setDraft({ id: null, channel: t.channel, name: t.name, subject: t.subject || "", body: t.body })
                    }
                  >
                    <Icon name="note" />
                  </button>
                ) : (
                  <>
                    <button className="tpl-icon-btn" title="Edit" onClick={() => setDraft({ ...t })}>
                      <Icon name="note" />
                    </button>
                    <button className="tpl-icon-btn tpl-del" title="Delete" onClick={() => remove(t.id)}>
                      <Icon name="x" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="tpl-empty">None yet.</div>
        )}
      </div>
    );
  };

  return (
    <div
      className="cmp-overlay"
      style={OVERLAY_STYLE}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="cmp-card tpl-card" role="dialog" aria-modal="true" style={CARD_STYLE}>
        <div className="cmp-head">
          <div className="cmp-title">
            <span className="cmp-badge cmp-badge-email"><Icon name="note" /></span>
            <div>
              <div className="cmp-h1">Message Templates</div>
              <div className="cmp-h2">Shared team library · used by Send text / Send email</div>
            </div>
          </div>
          <button className="cmp-x" onClick={onClose} type="button" aria-label="Close">
            <Icon name="x" />
          </button>
        </div>

        <div className="cmp-body tpl-body">
          <div className="tpl-cols">
            {list("text", "Text templates")}
            {list("email", "Email templates")}
          </div>

          <div className="tpl-editor">
            <div className="tpl-editor-head">{editing ? "Edit template" : "New template"}</div>
            <div className="tpl-row">
              <div className="tpl-seg">
                <button
                  className={`tpl-seg-btn${draft.channel === "text" ? " on" : ""}`}
                  onClick={() => setDraft({ ...draft, channel: "text" })}
                  type="button"
                >
                  Text
                </button>
                <button
                  className={`tpl-seg-btn${draft.channel === "email" ? " on" : ""}`}
                  onClick={() => setDraft({ ...draft, channel: "email" })}
                  type="button"
                >
                  Email
                </button>
              </div>
              <input
                className="cmp-input tpl-name"
                placeholder="Template name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>

            {isEmail ? (
              <input
                className="cmp-input"
                placeholder="Subject"
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                style={{ marginTop: 8 }}
              />
            ) : null}

            <textarea
              className="cmp-textarea"
              rows={isEmail ? 6 : 4}
              placeholder="Message… use {client}, {coordinator}, {moveDate}"
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              style={{ marginTop: 8 }}
            />
            <div className="tpl-hint">
              Placeholders: <code>{"{client}"}</code> <code>{"{coordinator}"}</code> <code>{"{moveDate}"}</code>
            </div>

            <div className="tpl-editor-foot">
              {editing ? (
                <button className="btn" type="button" onClick={() => setDraft(BLANK)}>New</button>
              ) : null}
              <button className="btn primary" type="button" onClick={save} disabled={!valid || busy}>
                <Icon name="check" /> {editing ? "Save changes" : "Add template"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
